import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReplaySubject, map, tap } from 'rxjs';
import { AiPromptService } from './ai-prompt.service';
import { AiElearnioLoaderService } from './ai-loader.service';
import { AiConfirmDialogComponent } from '@app/shared/ai-confirm-dialog/ai-confirm-dialog.component';
import { MemberService } from './member.service';
import { Member } from '@app/models/member.model';

// @ts-ignore
import { loadAiLearningContentDialogPlugin } from '@plugins/ai-learning-content.plugin.js';
// @ts-ignore
import { loadAiTranslatePlugin } from '@plugins/ai-translate.plugin.js';
// @ts-ignore
import { loadAiImprovePlugin } from '@plugins/ai-improve.plugin.js';

@Injectable({
  providedIn: 'root',
})
export class FroalaCommonService {
  private aiAgreementAccepted = false;
  private memberLanguage = '';
  private memberReady$ = new ReplaySubject<void>(1);
  private currentMember: Member | null = null;

  constructor(
    private loaderService: AiElearnioLoaderService,
    private aiPromptService: AiPromptService,
    private dialog: MatDialog,
    private memberService: MemberService,
    private ngZone: NgZone,
  ) {
    this.initMember();
  }

  public loadAiPlugins(): void {
    loadAiLearningContentDialogPlugin();
    loadAiTranslatePlugin();
    loadAiImprovePlugin();
  }

  private initMember(): void {
    this.memberService.current().subscribe((member: Member | null) => {
      if (member) {
        this.currentMember = member;
        this.aiAgreementAccepted = member.aiTermsAgreedTo ?? false;
        this.memberLanguage = member.language || '';
      }
      this.memberReady$.next();
    });
  }

  setupEvents(editor: any): void {
    this.ngZone.runOutsideAngular(() => {
      this.setupCommandInterceptors(editor);
      this.setupAIListeners(editor);
    });
  }

  private setupCommandInterceptors(editor: any): void {
    this.memberReady$.subscribe(() => {
      editor.events.on('commands.before', (cmd: string) => {
        const requiresAgreement = [
          'aiLearningContent',
          'aiTranslate',
          'aiImprove',
        ];
        if (!requiresAgreement.includes(cmd)) {
          return;
        }

        if (this.aiAgreementAccepted) {
          return true;
        }

        const dialogRef = this.dialog.open(AiConfirmDialogComponent, {
          width: '600px',
          panelClass: 'elearnio-mat-dialog-panel-class',
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.aiAgreementAccepted = true;
            this.currentMember!.aiTermsAgreedTo = true;
            this.currentMember!.save().subscribe(() => {});

            editor[cmd]?.openDialog();
          }
        });

        return false;
      });
    });
  }

  private setupAIListeners(editor: any): void {
    this.memberReady$.subscribe(() => {
      editor.events.on(
        'aiLearningContent.generateClicked',
        (data: { goal: string; audience: string }, editorInstance: any) => {
          const targetEditor = editorInstance || editor;
          this.loaderService.show();
          const selectedText = targetEditor.html.getSelected() || '';

          this.aiPromptService
            .getAiPrompt({
              text: selectedText,
              aigenerated: {
                goal: data.goal,
                audience: data.audience,
              },
              language: this.memberLanguage,
            })
            .pipe(
              map(response => response?.body?.response || selectedText),
              tap(() => this.loaderService.hide()),
            )
            .subscribe(generatedText => {
              this.ngZone.runOutsideAngular(() => {
                targetEditor.html.insert(generatedText, true);
                targetEditor.events.trigger('contentChanged');
              });
            });
        },
      );

      editor.events.on('aiImprove.itemClicked', (action: string) => {
        const selectedText = editor.html.getSelected();
        if (selectedText) {
          this.loaderService.show();

          this.aiPromptService
            .getAiPrompt({
              text: selectedText,
              improvement: action,
              language: this.memberLanguage,
            })
            .pipe(
              map(response => response?.body?.response || selectedText),
              tap(() => this.loaderService.hide()),
            )
            .subscribe(improvedText => {
              this.ngZone.runOutsideAngular(() => {
                editor.html.insert(improvedText, true);
                editor.events.trigger('contentChanged');
              });
            });
        }
      });

      editor.events.on('aiTranslate.itemClicked', (languageKey: string) => {
        const selectedText = editor.html.getSelected() || '';
        this.loaderService.show();

        this.aiPromptService
          .getAiPrompt({
            text: selectedText,
            translate: languageKey,
            language: this.memberLanguage,
          })
          .pipe(
            map(response => response?.body?.response || selectedText),
            tap(() => this.loaderService.hide()),
          )
          .subscribe(translatedText => {
            this.ngZone.runOutsideAngular(() => {
              editor.html.insert(translatedText, true);
              editor.events.trigger('contentChanged');
            });
          });
      });
    });
  }
}
